import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Document, Packer, Paragraph, TextRun, AlignmentType, HeadingLevel } from 'docx';

interface LandContractData {
  dealId?: string;
  contractDate: string;
  sellerName: string;
  sellerAddress: string;
  buyerName: string;
  buyerAddress: string;
  propertyCity: string;
  propertyCounty: string;
  propertyState: string;
  legalDescription: string;
  commonAddress: string;
  parcelId: string;
  salesPrice: string;
  downPayment: string;
  balance: string;
  interestRate: string;
  monthlyPayment: string;
  firstPaymentDate: string;
  termYears: string;
  lateFee: string;
  gracePeriodDays: string;
}

export function LandContractGenerator() {
  const { toast } = useToast();
  const [deals, setDeals] = useState<any[]>([]);
  const [formData, setFormData] = useState<LandContractData>(() => {
    const saved = localStorage.getItem('landContractProgress');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error loading saved form data:', e);
      }
    }
    return {
      contractDate: new Date().toISOString().split('T')[0],
      sellerName: "",
      sellerAddress: "",
      buyerName: "",
      buyerAddress: "",
      propertyCity: "",
      propertyCounty: "",
      propertyState: "Michigan",
      legalDescription: "",
      commonAddress: "",
      parcelId: "",
      salesPrice: "",
      downPayment: "",
      balance: "",
      interestRate: "",
      monthlyPayment: "",
      firstPaymentDate: "",
      termYears: "",
      lateFee: "60.00",
      gracePeriodDays: "7",
    };
  });

  useEffect(() => {
    loadDeals();
  }, []);

  useEffect(() => {
    localStorage.setItem('landContractProgress', JSON.stringify(formData));
  }, [formData]);

  const loadDeals = async () => {
    const { data, error } = await supabase
      .from('deals')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error loading deals:', error);
      return;
    }
    setDeals(data || []);
  };

  const loadDealData = async (dealId: string) => {
    const deal = deals.find(d => d.id === dealId);
    if (!deal) return;

    setFormData({
      ...formData,
      dealId,
      sellerName: deal.seller_name || "",
      sellerAddress: deal.seller_address || "",
      buyerName: deal.buyer_name || "",
      buyerAddress: deal.buyer_address || "",
      propertyCity: extractCity(deal.property_address),
      propertyCounty: extractCounty(deal.property_address),
      commonAddress: deal.property_address || "",
      salesPrice: deal.sales_price?.toString() || "",
      downPayment: deal.down_payment_amount?.toString() || "",
      balance: deal.beginning_balance?.toString() || "",
      interestRate: deal.interest_rate?.toString() || "",
      monthlyPayment: deal.payment_amount?.toString() || "",
      firstPaymentDate: deal.first_payment_date || "",
      termYears: deal.original_term_months ? (deal.original_term_months / 12).toString() : "",
    });
  };

  const extractCity = (address: string | null): string => {
    if (!address) return "";
    const parts = address.split(',');
    return parts.length > 1 ? parts[1].trim() : "";
  };

  const extractCounty = (address: string | null): string => {
    // Default or extract from deal data if available
    return "";
  };

  const clearForm = () => {
    const emptyForm: LandContractData = {
      contractDate: new Date().toISOString().split('T')[0],
      sellerName: "",
      sellerAddress: "",
      buyerName: "",
      buyerAddress: "",
      propertyCity: "",
      propertyCounty: "",
      propertyState: "Michigan",
      legalDescription: "",
      commonAddress: "",
      parcelId: "",
      salesPrice: "",
      downPayment: "",
      balance: "",
      interestRate: "",
      monthlyPayment: "",
      firstPaymentDate: "",
      termYears: "",
      lateFee: "60.00",
      gracePeriodDays: "7",
    };
    setFormData(emptyForm);
    localStorage.removeItem('landContractProgress');
    toast({
      title: "Form Cleared",
      description: "All fields have been reset",
    });
  };

  const generateDocument = async () => {
    if (!formData.sellerName || !formData.buyerName || !formData.propertyCity) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    try {
      const doc = new Document({
        sections: [{
          properties: {},
          children: [
            new Paragraph({
              text: "LAND CONTRACT",
              heading: HeadingLevel.HEADING_1,
              alignment: AlignmentType.CENTER,
              spacing: { after: 400 }
            }),
            
            new Paragraph({
              children: [
                new TextRun({ text: "Parties ", bold: true }),
                new TextRun(`This Contract, Made on ${formData.contractDate}, between ${formData.sellerName}, hereinafter referred to as the "Seller," whose address is ${formData.sellerAddress}, and ${formData.buyerName}, hereinafter referred to as the "Purchaser", whose address is ${formData.buyerAddress},`)
              ],
              spacing: { after: 200 }
            }),

            new Paragraph({
              text: "Witnesseth:",
              spacing: { after: 200 }
            }),

            new Paragraph({
              text: "Description of Premises",
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 200 }
            }),

            new Paragraph({
              text: "1. THE SELLER AGREES AS FOLLOWS:",
              spacing: { after: 200 }
            }),

            new Paragraph({
              text: "a) To sell and convey to the Purchaser:",
              spacing: { after: 100 }
            }),

            new Paragraph({
              text: `Land situated in the City of ${formData.propertyCity}, County of ${formData.propertyCounty}, State of ${formData.propertyState}`,
              spacing: { after: 200 }
            }),

            new Paragraph({
              text: formData.legalDescription,
              spacing: { after: 200 }
            }),

            new Paragraph({
              text: `Commonly known as: ${formData.commonAddress}`,
              spacing: { after: 100 }
            }),

            new Paragraph({
              text: `Parcel ID No. ${formData.parcelId}`,
              spacing: { after: 200 }
            }),

            new Paragraph({
              text: "This property may be located within the vicinity of farmland or a farm operation. Generally accepted agricultural and management practices which may generate noise, dust, odors, and other associated conditions may be used and are protected by the Michigan Right to Farm Act.",
              spacing: { after: 200 }
            }),

            new Paragraph({
              text: "Terms of Payment",
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 200 }
            }),

            new Paragraph({
              text: `That the consideration or sale of the above described premises the Purchaser ${formData.salesPrice ? `$${parseFloat(formData.salesPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''} (Sales Price), of which the sum of ${formData.downPayment ? `$${parseFloat(formData.downPayment).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''} (Amount Down) has heretofore been paid to the Seller, the receipt of which is hereby acknowledged, and the balance of ${formData.balance ? `$${parseFloat(formData.balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''} is to be paid to the Seller on any payment due.`,
              spacing: { after: 200 }
            }),

            new Paragraph({
              text: `This balance shall bear interest at the rate of ${formData.interestRate}% per annum. This balance shall be paid in monthly installments of ${formData.monthlyPayment ? `$${parseFloat(formData.monthlyPayment).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''} (Monthly Payment) each, or more at Purchaser's option, on the first day of each month beginning ${formData.firstPaymentDate}, said payments to be applied first upon interest and the balance on principal; PROVIDED, the entire purchase money and interest shall be fully paid within ${formData.termYears} years from the date hereof. In the event payments are not received within ${formData.gracePeriodDays} days from due date, a late fee of $${formData.lateFee} per payment shall be assessed.`,
              spacing: { after: 200 }
            }),
          ]
        }]
      });

      const blob = await Packer.toBlob(doc);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Land_Contract_${formData.buyerName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.docx`;
      link.click();
      window.URL.revokeObjectURL(url);

      localStorage.removeItem('landContractProgress');
      toast({
        title: "Success",
        description: "Land Contract generated successfully. Form progress cleared."
      });
    } catch (error) {
      console.error('Error generating document:', error);
      toast({
        title: "Error",
        description: "Failed to generate document",
        variant: "destructive"
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Land Contract Generator</span>
          <Button variant="outline" size="sm" onClick={clearForm}>
            Clear Form
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>Load from Deal (Optional)</Label>
          <Select onValueChange={loadDealData}>
            <SelectTrigger>
              <SelectValue placeholder="Select a deal..." />
            </SelectTrigger>
            <SelectContent>
              {deals.map(deal => (
                <SelectItem key={deal.id} value={deal.id}>
                  {deal.deal_title} - {deal.buyer_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Contract Date</Label>
            <Input
              type="date"
              value={formData.contractDate}
              onChange={(e) => setFormData({ ...formData, contractDate: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label>Seller Name *</Label>
            <Input
              value={formData.sellerName}
              onChange={(e) => setFormData({ ...formData, sellerName: e.target.value })}
              placeholder="John Doe"
            />
          </div>

          <div className="space-y-2">
            <Label>Seller Address</Label>
            <Input
              value={formData.sellerAddress}
              onChange={(e) => setFormData({ ...formData, sellerAddress: e.target.value })}
              placeholder="123 Main St, City, MI 48000"
            />
          </div>

          <div className="space-y-2">
            <Label>Buyer Name *</Label>
            <Input
              value={formData.buyerName}
              onChange={(e) => setFormData({ ...formData, buyerName: e.target.value })}
              placeholder="Jane Smith"
            />
          </div>

          <div className="space-y-2">
            <Label>Buyer Address</Label>
            <Input
              value={formData.buyerAddress}
              onChange={(e) => setFormData({ ...formData, buyerAddress: e.target.value })}
              placeholder="456 Oak Ave, City, MI 48000"
            />
          </div>

          <div className="space-y-2">
            <Label>Property City *</Label>
            <Input
              value={formData.propertyCity}
              onChange={(e) => setFormData({ ...formData, propertyCity: e.target.value })}
              placeholder="Ovid"
            />
          </div>

          <div className="space-y-2">
            <Label>Property County *</Label>
            <Input
              value={formData.propertyCounty}
              onChange={(e) => setFormData({ ...formData, propertyCounty: e.target.value })}
              placeholder="Clinton"
            />
          </div>

          <div className="space-y-2">
            <Label>Common Address</Label>
            <Input
              value={formData.commonAddress}
              onChange={(e) => setFormData({ ...formData, commonAddress: e.target.value })}
              placeholder="203 S. Main St, Ovid, MI 48866"
            />
          </div>

          <div className="space-y-2">
            <Label>Parcel ID</Label>
            <Input
              value={formData.parcelId}
              onChange={(e) => setFormData({ ...formData, parcelId: e.target.value })}
              placeholder="580-000-021-009-00"
            />
          </div>

          <div className="space-y-2">
            <Label>Sales Price</Label>
            <Input
              type="number"
              value={formData.salesPrice}
              onChange={(e) => setFormData({ ...formData, salesPrice: e.target.value })}
              placeholder="84900.00"
            />
          </div>

          <div className="space-y-2">
            <Label>Down Payment</Label>
            <Input
              type="number"
              value={formData.downPayment}
              onChange={(e) => setFormData({ ...formData, downPayment: e.target.value })}
              placeholder="12735.00"
            />
          </div>

          <div className="space-y-2">
            <Label>Balance</Label>
            <Input
              type="number"
              value={formData.balance}
              onChange={(e) => setFormData({ ...formData, balance: e.target.value })}
              placeholder="72165.00"
            />
          </div>

          <div className="space-y-2">
            <Label>Interest Rate (%)</Label>
            <Input
              type="number"
              step="0.01"
              value={formData.interestRate}
              onChange={(e) => setFormData({ ...formData, interestRate: e.target.value })}
              placeholder="5.5"
            />
          </div>

          <div className="space-y-2">
            <Label>Monthly Payment</Label>
            <Input
              type="number"
              value={formData.monthlyPayment}
              onChange={(e) => setFormData({ ...formData, monthlyPayment: e.target.value })}
              placeholder="1200.00"
            />
          </div>

          <div className="space-y-2">
            <Label>First Payment Date</Label>
            <Input
              type="date"
              value={formData.firstPaymentDate}
              onChange={(e) => setFormData({ ...formData, firstPaymentDate: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label>Term (Years)</Label>
            <Input
              type="number"
              value={formData.termYears}
              onChange={(e) => setFormData({ ...formData, termYears: e.target.value })}
              placeholder="4"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Legal Description</Label>
          <Textarea
            value={formData.legalDescription}
            onChange={(e) => setFormData({ ...formData, legalDescription: e.target.value })}
            placeholder="The West 38 feet of Lots 9 and 10, Block 21..."
            rows={6}
          />
        </div>

        <Button onClick={generateDocument} className="w-full">
          <Download className="mr-2 h-4 w-4" />
          Generate Land Contract
        </Button>
      </CardContent>
    </Card>
  );
}
